/**
 * My Cases tab — accountant's currently-claimed cases.
 * Extracted from pages/AccountantQueue.tsx during Task 4.
 *
 * Signed-off cases are excluded here and rendered by SignedOffTab so the two
 * tabs don't overlap.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';
import { AccountantTabProps, CaseSummary, QueueResponse } from './types';
import AccountantCaseRow from './AccountantCaseRow';
import TabShell, { StatusFilterValue } from './TabShell';
import { useCaseFilter } from './useCaseFilter';

// Keep in sync with SignedOffTab's predicate. `signed_off` (boolean set by
// POST /api/v1/case-signoff/*) is the authoritative field. `workflow_status`
// check is a defensive fallback for any legacy shape.
const isSignedOff = (c: CaseSummary) =>
  c.signed_off === true || c.workflow_status === 'signed_off';

export default function MyCasesTab({
  selectedCaseId,
  onSelectCase,
  onRequestSignOff,
  signingOffCaseId,
  currentAccountantId,
  onVisibleCaseIdsChange,
}: AccountantTabProps) {
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [releasingCase, setReleasingCase] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<QueueResponse>(
        `/api/v1/queue/accountant/my-cases?page=${page}&page_size=20`,
      );
      // Active (not-yet-signed) cases only.
      const all = Array.isArray(data.cases) ? data.cases : [];
      const active = all.filter((c) => !isSignedOff(c));
      setCases(active);
      setTotalCount(active.length);
      setTotalPages(data.total_pages || 1);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load my cases');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const lastNotifiedRef = useRef<string>('');
  useEffect(() => {
    const ids = cases.map((c) => c.id);
    const key = ids.join('|');
    if (key !== lastNotifiedRef.current) {
      lastNotifiedRef.current = key;
      onVisibleCaseIdsChange?.(ids);
    }
  }, [cases, onVisibleCaseIdsChange]);

  const handleRelease = async (e: React.MouseEvent, caseItem: CaseSummary) => {
    e.stopPropagation();
    setReleasingCase(caseItem.id);
    try {
      await api.post(`/api/v1/queue/accountant/release/${caseItem.id}`, {});
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to release case');
    } finally {
      setReleasingCase(null);
    }
  };

  const handleSignOff = async (e: React.MouseEvent, caseItem: CaseSummary) => {
    e.stopPropagation();
    // Parent owns the sign-off flow so the success toast survives tab switches.
    await onRequestSignOff(caseItem);
    // Parent triggers a global refresh — but also refresh this tab to drop the
    // signed case out of the list immediately.
    await fetchData();
  };

  const filteredCases = useCaseFilter(cases, searchQuery, statusFilter);

  return (
    <TabShell
      tabPanelId="tabpanel-mine"
      tabPanelLabelledBy="tab-mine"
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      heading="My Claimed Cases"
      filteredCount={filteredCases.length}
      totalCount={totalCount}
      loading={loading}
      error={error}
      onDismissError={() => setError(null)}
      onRetry={fetchData}
      page={page}
      totalPages={totalPages}
      onPrevPage={() => setPage(Math.max(1, page - 1))}
      onNextPage={() => setPage(Math.min(totalPages, page + 1))}
      isEmpty={filteredCases.length === 0}
      emptyMessage={
        searchQuery || statusFilter !== 'all'
          ? 'No cases match your search criteria. Try adjusting your filters.'
          : "You haven't claimed any cases yet. Go to the Queue tab to claim one."
      }
    >
      {filteredCases.map((caseItem) => (
        <AccountantCaseRow
          key={caseItem.id}
          caseItem={caseItem}
          mode="mine"
          isSelected={selectedCaseId === caseItem.id}
          // Strict select (BUG-4) — see QueueTab for rationale.
          onSelect={(id) => onSelectCase(id)}
          currentAccountantId={currentAccountantId}
          onRelease={handleRelease}
          onSignOff={handleSignOff}
          releasingCaseId={releasingCase}
          signingOffCaseId={signingOffCaseId}
        />
      ))}
    </TabShell>
  );
}
